import React, { useContext } from 'react';
import { Label } from '@rebass/forms';
import { useFormikContext } from 'formik';
import { Box } from 'rebass';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/src/style.css';

import { ErrorMessage } from './ErrorMessage';
import { FocusContext } from '../../../contexts/FocusContext';
import {
  ensureFourDigitYear,
  formatDateObject,
  parseDateFromString,
} from '../../../lib/formatDateTimes';
import { colors, forms } from '../../../theme';
import { startOfToday, subYears } from 'date-fns';
import { DateUtils, DayPickerInputProps } from 'react-day-picker';

const dateFormatTokens = 'M/d/yyyy';
export const dateFieldInputRegex = /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/;
export const parseDate = (dateString: string) => {
  if (!dateFieldInputRegex.test(dateString)) return;

  dateString = ensureFourDigitYear(dateString);
  const parsedDate = parseDateFromString(dateString, dateFormatTokens);
  if (DateUtils.isDate(parsedDate)) {
    return parsedDate;
  }
  return;
};

export interface DateFieldProps extends DayPickerInputProps {
  name: string;
  label: string;
  required?: boolean;
  validateOnBlur?: boolean;
}

export const DateField: React.FC<DateFieldProps> = props => {
  const {
    name,
    label,
    placeholder = 'mm/dd/yyyy',
    required,
    validateOnBlur,
  } = props;
  let {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
  } = useFormikContext<any>();
  const { setFocus } = useContext(FocusContext);

  const hasError = !!touched[name] && errors && errors[name];
  const errorName = `errorFor${name[0].toUpperCase() + name.substr(1)}`;

  return (
    <Box
      sx={{
        input: {
          borderColor: hasError ? colors.red : forms.input.borderColor,
        },
      }}
    >
      <Label htmlFor={name} variant="label" id={`label-${name}`}>
        {label}
      </Label>
      <DayPickerInput
        {...(values[name] ? { value: values[name] } : {})}
        aria-describedby={errorName}
        aria-required={required}
        inputProps={{
          id: name,
          'data-testid': name,
          name,
          onChange: handleChange,
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            setFocus(false);
            if (validateOnBlur) {
              handleBlur(e);
            }

            const dateString = e.currentTarget.value;
            const parsedDate = parseDate(dateString);
            if (parsedDate) setFieldValue(name, parseDate(dateString));
          },
          onFocus: () => setFocus(true),
        }}
        dayPickerProps={{
          disabledDays: [
            {
              after: startOfToday(),
              before: subYears(startOfToday(), 3),
            },
          ],
          labels: {
            previousMonth: 'Prev',
            nextMonth: 'Next',
          },
          modifiers: {
            today: new Date(),
          },
          modifiersStyles: {
            today: {
              backgroundColor: colors.mist,
              color: colors.ashGray,
              fontWeight: 'normal',
            },
          },
        }}
        formatDate={(date: Date) => formatDateObject(date, 'MM/dd/yyyy')}
        format={dateFormatTokens}
        placeholder={placeholder}
        parseDate={(dateString: string) => {
          if (!DateUtils.isDate(new Date(dateString))) {
            return;
          } else {
            return new Date(dateString);
          }
        }}
      />

      {hasError && (
        <ErrorMessage name={name} errors={errors} errorName={errorName} />
      )}
    </Box>
  );
};
